import { PDFDocument } from 'pdf-lib';
import { OBJ } from '../constants';
import { findImagePlacements, swapImageXObject } from './stripText';

/**
 * Replacing a picture that is already in the PDF.
 *
 * Covering it and pasting a new one on top is what every annotation tool does,
 * and it is not the same thing: the old photograph is still in the file, it
 * still weighs what it weighed, it still comes out of any extractor, and the
 * cover shows as a seam wherever the viewer interpolates. This swaps the
 * picture itself — the page's own `Do` keeps its matrix, so the new image lands
 * exactly where the old one was, and the old bytes go with it.
 *
 * The identity of a picture is WHERE IT IS PAINTED, not its resource name: one
 * XObject can be painted twice at two sizes. So a swap records the rect the
 * user clicked, and the exporter matches that rect back to a `Do` in the
 * content stream.
 */

/** How close a recorded rect must be to a placement to be the same picture. */
const MATCH_TOLERANCE = 1.5;

/** User-space point back into page view space (inverse of `mapPoint`). */
function unmapPoint(x, y, rotation, box) {
    const { x: cx, y: cy, width: W, height: H } = box;
    switch (rotation) {
        case 90: return [y - cy, x - cx];
        case 180: return [cx + W - x, y - cy];
        case 270: return [cy + H - y, cx + W - x];
        default: return [x - cx, cy + H - y];
    }
}

/**
 * Every image on every page, in the page's own view space.
 *
 * One pdf-lib load for the whole document: the walk itself is cheap, opening
 * the file is not, and the editor asks for this the moment the tool is armed.
 *
 * @returns {Promise<Record<string, Array<{id,name,x,y,w,h}>>>} keyed by page uid
 */
export async function readImagePlacements(sourceBytes, pages) {
    if (!sourceBytes || !pages?.length) return {};
    const doc = await PDFDocument.load(sourceBytes.slice(0), { ignoreEncryption: true });
    const sourcePages = doc.getPages();
    const out = {};

    for (const meta of pages) {
        if (meta.sourceIndex == null) continue;
        const page = sourcePages[meta.sourceIndex];
        if (!page) continue;

        const rotation = ((meta.baseRotation || 0) % 360 + 360) % 360;
        const cropBox = page.getCropBox();
        const box = { x: cropBox.x, y: cropBox.y, width: cropBox.width, height: cropBox.height };

        out[meta.uid] = findImagePlacements(page).map((placement, index) => {
            const [ax, ay] = unmapPoint(placement.x0, placement.y0, rotation, box);
            const [bx, by] = unmapPoint(placement.x1, placement.y1, rotation, box);
            return {
                id: `${meta.uid}-img-${index}`,
                name: placement.name,
                x: Math.min(ax, bx),
                y: Math.min(ay, by),
                w: Math.abs(bx - ax),
                h: Math.abs(by - ay),
            };
        // A hairline rule drawn as a 1×1 image is not a picture anybody means
        // to replace, and it would litter the page with unclickable slivers.
        }).filter((r) => r.w > 6 && r.h > 6);
    }

    return out;
}

/** The swap objects on a page. */
export const swapsOf = (objects) => (objects || []).filter((o) => o.type === OBJ.IMAGE_SWAP);

/**
 * Apply this page's swaps to a pdf-lib page.
 *
 * Runs against the page as the source left it — before any resize — because
 * that is the space `replacesImage` was measured in.
 *
 * @returns {Promise<number>} how many pictures were replaced
 */
export async function applyImageSwaps(outDoc, page, meta, objects) {
    const swaps = swapsOf(objects);
    if (!swaps.length) return 0;

    const rotation = ((meta.baseRotation || 0) % 360 + 360) % 360;
    const cropBox = page.getCropBox();
    const box = { x: cropBox.x, y: cropBox.y, width: cropBox.width, height: cropBox.height };

    const placements = findImagePlacements(page).map((placement) => {
        const [ax, ay] = unmapPoint(placement.x0, placement.y0, rotation, box);
        const [bx, by] = unmapPoint(placement.x1, placement.y1, rotation, box);
        return {
            name: placement.name,
            x: Math.min(ax, bx),
            y: Math.min(ay, by),
            w: Math.abs(bx - ax),
            h: Math.abs(by - ay),
        };
    });

    let done = 0;
    for (const swap of swaps) {
        const target = swap.replacesImage;
        const match = placements.find((p) => Math.abs(p.x - target.x) < MATCH_TOLERANCE
            && Math.abs(p.y - target.y) < MATCH_TOLERANCE
            && Math.abs(p.w - target.w) < MATCH_TOLERANCE
            && Math.abs(p.h - target.h) < MATCH_TOLERANCE);
        if (!match) continue;

        try {
            const bytes = dataUrlToBytes(swap.src);
            // eslint-disable-next-line no-await-in-loop
            const embedded = /^data:image\/jpe?g/i.test(swap.src)
                ? await outDoc.embedJpg(bytes)
                : await outDoc.embedPng(bytes);
            if (swapImageXObject(page, match.name, embedded.ref)) done += 1;
        } catch {
            /* an image this cannot embed leaves the original in place */
        }
    }
    return done;
}

function dataUrlToBytes(dataUrl) {
    const base64 = String(dataUrl).split(',')[1] || '';
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
    return bytes;
}

/**
 * Fit a picture into the shape the old one was drawn at.
 *
 * The page's matrix stretches whatever it is given across the old rect, so a
 * portrait photograph dropped onto a landscape logo comes out squashed. Letter-
 * boxing it onto a canvas of the OLD aspect ratio first is what keeps the new
 * picture in proportion without touching the page.
 *
 * `fill` crops to the box instead, for when the new picture is meant to cover
 * it — which is what people expect of a photograph swap.
 */
export async function fitImageToBox(src, boxW, boxH, mode = 'contain') {
    // `createImageBitmap` decodes off the main thread and does not wait to be
    // painted. `<img>.decode()` does — a tab that is not on screen can leave it
    // pending indefinitely, which reads as "choosing a file did nothing".
    const image = await decodeImage(src);

    const aspect = boxW / boxH;
    // Enough pixels for print at the size it is drawn, capped so a swap cannot
    // make the file enormous.
    const width = Math.min(2400, Math.max(image.width, Math.round(boxW * 4)));
    const height = Math.max(1, Math.round(width / aspect));

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');

    const scale = mode === 'fill'
        ? Math.max(width / image.width, height / image.height)
        : Math.min(width / image.width, height / image.height);
    const drawW = image.width * scale;
    const drawH = image.height * scale;

    if (mode !== 'fill') {
        // The letterbox has to be painted: a PNG's transparent margin would let
        // the page show through, and the page behind a picture is not blank.
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, width, height);
    }
    ctx.drawImage(image.image || image, (width - drawW) / 2, (height - drawH) / 2, drawW, drawH);

    return { src: canvas.toDataURL('image/png'), width, height };
}

/** An image with `width`/`height`, whichever decoder this browser offers. */
async function decodeImage(src) {
    if (typeof createImageBitmap === 'function') {
        try {
            const blob = await (await fetch(src)).blob();
            return await createImageBitmap(blob);
        } catch {
            /* fall through to the element */
        }
    }
    const image = new Image();
    image.src = src;
    await new Promise((resolve, reject) => {
        if (image.complete && image.naturalWidth) { resolve(); return; }
        image.onload = resolve;
        image.onerror = reject;
    });
    return { width: image.naturalWidth, height: image.naturalHeight, image };
}
